import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useToast } from '@/hooks/use-toast';
import {
  Radio,
  Search,
  Shield,
  ShieldCheck,
  ShieldAlert,
  ShieldQuestion,
  Flame,
  Heart,
  Siren,
  Globe,
  Building2,
  ThumbsUp,
  ThumbsDown,
  Plus,
  Info,
  ExternalLink,
  Database,
  Users,
  CheckCircle2,
  AlertCircle,
  ChevronRight,
  Loader2,
  Copy,
  Check,
  MapPin,
  Lock,
  Zap
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Verification status badges
const STATUS_CONFIG = {
  official: { icon: ShieldCheck, color: 'bg-emerald-600', text: 'Official', desc: 'Federal standard - 100% reliable' },
  verified: { icon: Shield, color: 'bg-blue-600', text: 'Verified', desc: 'Community confirmed active' },
  likely_valid: { icon: CheckCircle2, color: 'bg-teal-600', text: 'Likely Valid', desc: 'More confirmations than disputes' },
  unverified: { icon: ShieldQuestion, color: 'bg-amber-600', text: 'Unverified', desc: 'Needs community verification' },
  likely_invalid: { icon: AlertCircle, color: 'bg-orange-600', text: 'Likely Invalid', desc: 'More disputes than confirmations' },
  disputed: { icon: ShieldAlert, color: 'bg-red-600', text: 'Disputed', desc: 'Community reports inaccurate' }
};

// Source badges
const SOURCE_CONFIG = {
  nifog: { icon: Globe, color: 'bg-emerald-700', text: 'NIFOG', desc: 'National Interoperability Field Operations Guide' },
  fcc: { icon: Building2, color: 'bg-blue-700', text: 'FCC', desc: 'FCC Universal Licensing System' },
  community: { icon: Users, color: 'bg-purple-700', text: 'Community', desc: 'User submitted frequency' }
};

// Service type icons
const SERVICE_ICONS = {
  police: Siren,
  fire: Flame,
  ems: Heart,
  dispatch: Radio,
  interop: Globe,
  other: Radio
};

const SERVICE_COLORS = {
  police: 'text-blue-400',
  fire: 'text-orange-400',
  ems: 'text-red-400',
  dispatch: 'text-purple-400',
  interop: 'text-emerald-400',
  other: 'text-slate-400'
};

// Frequency card component
const FrequencyCard = ({ freq, onVote, isAuthenticated }) => {
  const [copied, setCopied] = useState(false);
  const statusConfig = STATUS_CONFIG[freq.verification_status] || STATUS_CONFIG.unverified;
  const sourceConfig = SOURCE_CONFIG[freq.source] || SOURCE_CONFIG.community;
  const ServiceIcon = SERVICE_ICONS[freq.service_type] || Radio;
  
  const copyFrequency = () => {
    navigator.clipboard.writeText(freq.frequency.toFixed(4));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <div 
      data-testid={`frequency-card-${freq.frequency}`}
      className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 hover:border-teal-500/50 transition-all"
    >
      <div className="flex items-start justify-between gap-4">
        {/* Left: Frequency info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <ServiceIcon className={`w-4 h-4 ${SERVICE_COLORS[freq.service_type]}`} />
            <button 
              onClick={copyFrequency}
              className="font-mono text-lg font-bold text-teal-400 hover:text-teal-300 flex items-center gap-1"
            >
              {freq.frequency.toFixed(4)} MHz
              {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3 opacity-50" />}
            </button>
            {freq.alpha_tag && (
              <span className="text-xs text-slate-400 font-mono">[{freq.alpha_tag}]</span>
            )}
          </div>
          
          <p className="text-sm text-slate-300 mb-2">{freq.description}</p>
          
          <div className="flex flex-wrap gap-1.5 text-xs">
            {freq.agency && (
              <span className="px-2 py-0.5 bg-slate-700 rounded text-slate-300">{freq.agency}</span>
            )}
            {freq.city && (
              <span className="px-2 py-0.5 bg-slate-700/50 rounded text-slate-400">{freq.city}, {freq.state}</span>
            )}
            {freq.mode && (
              <span className="px-2 py-0.5 bg-slate-700/50 rounded text-slate-400">{freq.mode}</span>
            )}
            {freq.tone && (
              <span className="px-2 py-0.5 bg-slate-700/50 rounded text-amber-400/70">{freq.tone}</span>
            )}
          </div>
        </div>
        
        {/* Right: Status and voting */}
        <div className="flex flex-col items-end gap-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <Badge className={`${statusConfig.color} text-white text-xs`}>
                  <statusConfig.icon className="w-3 h-3 mr-1" />
                  {statusConfig.text}
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                <p>{statusConfig.desc}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <Badge variant="outline" className="text-xs border-slate-600">
                  <sourceConfig.icon className="w-3 h-3 mr-1" />
                  {sourceConfig.text}
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                <p>{sourceConfig.desc}</p>
                {freq.source_url && (
                  <a href={freq.source_url} target="_blank" rel="noopener noreferrer" className="text-teal-400 flex items-center gap-1 mt-1">
                    View source <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          {/* Confidence score */}
          <div className="flex items-center gap-1 text-xs">
            <span className={`font-medium ${
              freq.confidence_score >= 80 ? 'text-emerald-400' :
              freq.confidence_score >= 60 ? 'text-amber-400' :
              'text-red-400'
            }`}>
              {freq.confidence_score}%
            </span>
            <span className="text-slate-500">confidence</span>
          </div>
          
          {/* Voting (only for non-official sources) */}
          {freq.source !== 'nifog' && isAuthenticated && (
            <div className="flex items-center gap-1">
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-emerald-400 hover:bg-emerald-400/20"
                onClick={() => onVote(freq, 'confirm')}
              >
                <ThumbsUp className="w-3 h-3 mr-1" />
                {freq.confirmations || 0}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-red-400 hover:bg-red-400/20"
                onClick={() => onVote(freq, 'dispute')}
              >
                <ThumbsDown className="w-3 h-3 mr-1" />
                {freq.disputes || 0}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Submit frequency modal
const SubmitFrequencyModal = ({ states, onSubmit, isOpen, onOpenChange }) => {
  const [formData, setFormData] = useState({
    frequency: '',
    description: '',
    alpha_tag: '',
    mode: 'FM',
    tone: '',
    service_type: 'police',
    state: '',
    county: '',
    city: '',
    agency: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  
  const handleSubmit = async () => {
    if (!formData.frequency || !formData.description || !formData.state) return;
    
    setSubmitting(true);
    try {
      await onSubmit({
        ...formData,
        frequency: parseFloat(formData.frequency)
      });
      setFormData({
        frequency: '',
        description: '',
        alpha_tag: '',
        mode: 'FM',
        tone: '',
        service_type: 'police',
        state: '',
        county: '',
        city: '',
        agency: '',
        notes: ''
      });
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] bg-slate-900 border-slate-700 text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5 text-teal-400" />
            Submit New Frequency
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Help grow the community database. Your submission will be verified by other users.
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Frequency (MHz) *</label>
              <Input
                placeholder="155.4750"
                value={formData.frequency}
                onChange={(e) => setFormData({...formData, frequency: e.target.value})}
                className="bg-slate-800 border-slate-700"
              />
            </div>
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Mode</label>
              <Select value={formData.mode} onValueChange={(v) => setFormData({...formData, mode: v})}>
                <SelectTrigger className="bg-slate-800 border-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="FM">FM</SelectItem>
                  <SelectItem value="NFM">NFM (Narrowband)</SelectItem>
                  <SelectItem value="AM">AM</SelectItem>
                  <SelectItem value="P25">P25 Digital</SelectItem>
                  <SelectItem value="DMR">DMR Digital</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div>
            <label className="text-sm text-slate-400 mb-1 block">Description *</label>
            <Input
              placeholder="County Fire Dispatch"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="bg-slate-800 border-slate-700"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Alpha Tag</label>
              <Input
                placeholder="COFD-DISP"
                value={formData.alpha_tag}
                onChange={(e) => setFormData({...formData, alpha_tag: e.target.value})}
                className="bg-slate-800 border-slate-700"
              />
            </div>
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Tone (PL/DCS)</label>
              <Input
                placeholder="103.5 PL"
                value={formData.tone}
                onChange={(e) => setFormData({...formData, tone: e.target.value})}
                className="bg-slate-800 border-slate-700"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Service Type</label>
              <Select value={formData.service_type} onValueChange={(v) => setFormData({...formData, service_type: v})}>
                <SelectTrigger className="bg-slate-800 border-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="police">Police</SelectItem>
                  <SelectItem value="fire">Fire</SelectItem>
                  <SelectItem value="ems">EMS</SelectItem>
                  <SelectItem value="dispatch">Dispatch</SelectItem>
                  <SelectItem value="interop">Interoperability</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-slate-400 mb-1 block">State *</label>
              <Select value={formData.state} onValueChange={(v) => setFormData({...formData, state: v})}>
                <SelectTrigger className="bg-slate-800 border-slate-700">
                  <SelectValue placeholder="Select state" />
                </SelectTrigger>
                <SelectContent className="max-h-[200px]">
                  {states.map(s => (
                    <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-slate-400 mb-1 block">City</label>
              <Input
                placeholder="Los Angeles"
                value={formData.city}
                onChange={(e) => setFormData({...formData, city: e.target.value})}
                className="bg-slate-800 border-slate-700"
              />
            </div>
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Agency</label>
              <Input
                placeholder="LAPD"
                value={formData.agency}
                onChange={(e) => setFormData({...formData, agency: e.target.value})}
                className="bg-slate-800 border-slate-700"
              />
            </div>
          </div>
          
          <div>
            <label className="text-sm text-slate-400 mb-1 block">Notes</label>
            <Input
              placeholder="Any additional info..."
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              className="bg-slate-800 border-slate-700"
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={submitting || !formData.frequency || !formData.description || !formData.state}
            className="bg-teal-600 hover:bg-teal-700"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
            Submit Frequency
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Data transparency panel
const TransparencyPanel = ({ stats }) => {
  if (!stats) return null;
  
  return (
    <div className="bg-slate-800/30 border border-slate-700 rounded-lg p-4 mb-6">
      <div className="flex items-center gap-2 mb-3">
        <Database className="w-5 h-5 text-teal-400" />
        <h3 className="font-semibold text-white">Data Transparency</h3>
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <div className="text-center">
          <div className="text-2xl font-bold text-white">{stats.total_frequencies}</div>
          <div className="text-xs text-slate-400">Total Frequencies</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-emerald-400">{stats.by_source?.nifog_official || 0}</div>
          <div className="text-xs text-slate-400">NIFOG Official</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-blue-400">{stats.by_source?.fcc_licensed || 0}</div>
          <div className="text-xs text-slate-400">FCC Licensed</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-purple-400">{stats.by_source?.community_submitted || 0}</div>
          <div className="text-xs text-slate-400">Community</div>
        </div>
      </div>
      
      <div className="text-xs text-slate-500 flex items-start gap-2">
        <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <span>{stats.transparency_note}</span>
      </div>
      
      {stats.data_sources && (
        <div className="mt-4 pt-4 border-t border-slate-700">
          <p className="text-xs text-slate-400 mb-2">Data Sources:</p>
          <div className="flex flex-wrap gap-2">
            {stats.data_sources.map((source, idx) => (
              <TooltipProvider key={idx}>
                <Tooltip>
                  <TooltipTrigger>
                    <Badge variant="outline" className="text-xs border-slate-600">
                      {source.name}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[250px]">
                    <p className="font-medium">{source.full_name}</p>
                    <p className="text-slate-400 text-xs mt-1">Authority: {source.authority}</p>
                    <p className="text-emerald-400 text-xs">{source.reliability}</p>
                    {source.url && (
                      <a href={source.url} target="_blank" rel="noopener noreferrer" className="text-teal-400 text-xs flex items-center gap-1 mt-1">
                        View source <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Locked state frequencies component for free users
const LockedStateFrequencies = () => (
  <div className="text-center py-12 bg-slate-800/30 rounded-xl border border-slate-700">
    <div className="inline-flex p-4 bg-teal-500/20 rounded-full mb-4">
      <Lock className="w-10 h-10 text-teal-400" />
    </div>
    <h3 className="text-xl font-bold text-white mb-2">State Frequencies are Pro</h3>
    <p className="text-slate-400 mb-4 max-w-md mx-auto">
      Free accounts get access to national NIFOG interoperability channels. 
      Upgrade to Pro for full access to frequencies in all 50 states.
    </p>
    <Link to="/pricing">
      <Button className="bg-teal-600 hover:bg-teal-700">
        <Zap className="w-4 h-4 mr-2" />
        Upgrade to Pro - $10/month
      </Button>
    </Link>
    <p className="text-xs text-slate-500 mt-2">
      Or $80/year (save $40) • $199 lifetime
    </p>
  </div>
);

// Main component
const FrequencyBrowser = () => {
  const { token } = useAuth();
  const { getFrequencyAccess, isPaid } = useSubscription();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('national');
  const [selectedState, setSelectedState] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [submitModalOpen, setSubmitModalOpen] = useState(false);
  
  const [stats, setStats] = useState(null);
  const [states, setStates] = useState([]);
  const [frequencies, setFrequencies] = useState([]);
  const [nationalFreqs, setNationalFreqs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  
  // Check if user can access state frequencies
  const canAccessAllStates = getFrequencyAccess() === 'all';
  
  const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
  
  // Fetch stats
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await fetch(`${API_URL}/api/frequencies/stats`);
        if (res.ok) {
          const data = await res.json();
          setStats(data);
        }
      } catch (err) {
        console.error('Failed to fetch stats:', err);
      }
    };
    fetchStats();
  }, []);
  
  // Fetch states
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const res = await fetch(`${API_URL}/api/frequencies/states`);
        if (res.ok) {
          const data = await res.json();
          setStates(data.states || []);
        }
      } catch (err) {
        console.error('Failed to fetch states:', err);
      }
    };
    fetchStates();
  }, []);
  
  // Fetch national frequencies
  useEffect(() => {
    const fetchNational = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/frequencies/national`);
        if (res.ok) {
          const data = await res.json();
          setNationalFreqs(data.frequencies || []);
        }
      } catch (err) {
        console.error('Failed to fetch national frequencies:', err);
      } finally {
        setLoading(false);
      }
    };
    
    if (activeTab === 'national') {
      fetchNational();
    }
  }, [activeTab]);
  
  // Fetch state frequencies
  useEffect(() => {
    if (!selectedState || activeTab !== 'browse') return;
    
    const fetchStateFreqs = async () => {
      setLoading(true);
      try {
        let url = `${API_URL}/api/frequencies/state/${selectedState}`;
        if (serviceFilter !== 'all') {
          url += `?service_type=${serviceFilter}`;
        }
        const res = await fetch(url);
        if (res.ok) {
          const data = await res.json();
          setFrequencies(data.frequencies || []);
        }
      } catch (err) {
        console.error('Failed to fetch state frequencies:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchStateFreqs();
  }, [selectedState, serviceFilter, activeTab]);
  
  // Search handler
  const handleSearch = useCallback(async () => {
    if (!searchQuery || searchQuery.length < 2) return;
    
    setLoading(true);
    try {
      let url = `${API_URL}/api/frequencies/search?q=${encodeURIComponent(searchQuery)}`;
      if (selectedState) url += `&state=${selectedState}`;
      if (serviceFilter !== 'all') url += `&service_type=${serviceFilter}`;
      
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setSearchResults(data);
        setActiveTab('search');
      }
    } catch (err) {
      console.error('Search failed:', err);
      toast({
        title: 'Search failed',
        description: 'Could not complete search. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [searchQuery, selectedState, serviceFilter, toast]);
  
  // Vote handler
  const handleVote = async (freq, voteType) => {
    if (!token) {
      toast({
        title: 'Login required',
        description: 'Please log in to vote on frequencies',
        variant: 'destructive'
      });
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/frequencies/${freq.id}/vote`, {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ vote: voteType })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast({
          title: 'Vote recorded',
          description: `Thank you for ${voteType === 'confirm' ? 'confirming' : 'disputing'} this frequency`,
        });
        
        // Refresh data
        if (activeTab === 'search' && searchResults) {
          handleSearch();
        } else if (activeTab === 'browse' && selectedState) {
          // Trigger re-fetch
          setSelectedState(s => s);
        }
      } else {
        const err = await res.json();
        toast({
          title: 'Vote failed',
          description: err.detail || 'Could not record vote',
          variant: 'destructive'
        });
      }
    } catch (err) {
      console.error('Vote failed:', err);
    }
  };
  
  // Submit handler
  const handleSubmit = async (freqData) => {
    try {
      const res = await fetch(`${API_URL}/api/frequencies/submit`, {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(freqData)
      });
      
      if (res.ok) {
        toast({
          title: 'Frequency submitted',
          description: 'Your frequency has been submitted for community verification',
        });
      } else {
        const err = await res.json();
        toast({
          title: 'Submission failed',
          description: err.detail || 'Could not submit frequency',
          variant: 'destructive'
        });
      }
    } catch (err) {
      console.error('Submit failed:', err);
      toast({
        title: 'Submission failed',
        description: 'Network error. Please try again.',
        variant: 'destructive'
      });
    }
  };
  
  // Get displayed frequencies based on active tab
  const displayedFrequencies = activeTab === 'search' 
    ? (searchResults?.frequencies || [])
    : activeTab === 'national'
    ? nationalFreqs
    : frequencies;
  
  const filteredDisplay = serviceFilter === 'all' 
    ? displayedFrequencies 
    : displayedFrequencies.filter(f => f.service_type === serviceFilter);
  
  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="frequency-browser">
      {/* Header */}
      <div className="border-b border-slate-800 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Radio className="w-6 h-6 text-teal-400" />
                Frequency Database
              </h1>
              <p className="text-slate-400 text-sm mt-1">
                Free, transparent, community-verified public safety frequencies
              </p>
            </div>
            
            {token && (
              <div className="flex items-center gap-3">
                <Link to="/frequencies/browse">
                  <Button variant="outline" className="border-slate-600">
                    <MapPin className="w-4 h-4 mr-2" />
                    Browse All States
                  </Button>
                </Link>
                <Button 
                  onClick={() => setSubmitModalOpen(true)}
                  className="bg-teal-600 hover:bg-teal-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Submit Frequency
                </Button>
              </div>
            )}
            {!token && (
              <Link to="/frequencies/browse">
                <Button variant="outline" className="border-slate-600">
                  <MapPin className="w-4 h-4 mr-2" />
                  Browse All States
                </Button>
              </Link>
            )}
          </div>
          
          {/* Search bar */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by frequency, agency, city, or description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-10 bg-slate-800 border-slate-700"
                data-testid="frequency-search-input"
              />
            </div>
            <Select value={serviceFilter} onValueChange={setServiceFilter}>
              <SelectTrigger className="w-[150px] bg-slate-800 border-slate-700">
                <SelectValue placeholder="All types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="police">Police</SelectItem>
                <SelectItem value="fire">Fire</SelectItem>
                <SelectItem value="ems">EMS</SelectItem>
                <SelectItem value="dispatch">Dispatch</SelectItem>
                <SelectItem value="interop">Interop</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={handleSearch} disabled={loading || searchQuery.length < 2}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </div>
      
      {/* Main content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Transparency panel */}
        <TransparencyPanel stats={stats} />
        
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="bg-slate-800/50 border border-slate-700">
            <TabsTrigger value="national" className="data-[state=active]:bg-teal-600">
              <Globe className="w-4 h-4 mr-2" />
              National Interop
            </TabsTrigger>
            <TabsTrigger value="browse" className="data-[state=active]:bg-teal-600">
              {canAccessAllStates ? (
                <Building2 className="w-4 h-4 mr-2" />
              ) : (
                <Lock className="w-4 h-4 mr-2 text-amber-400" />
              )}
              Browse by State
              {!canAccessAllStates && (
                <Badge className="ml-2 bg-amber-600/50 text-amber-200 text-[10px]">PRO</Badge>
              )}
            </TabsTrigger>
            {searchResults && (
              <TabsTrigger value="search" className="data-[state=active]:bg-teal-600">
                <Search className="w-4 h-4 mr-2" />
                Search Results ({searchResults.total})
              </TabsTrigger>
            )}
          </TabsList>
          
          {/* National tab */}
          <TabsContent value="national" className="space-y-4">
            <div className="bg-emerald-900/20 border border-emerald-800 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck className="w-5 h-5 text-emerald-400" />
                <h3 className="font-semibold text-emerald-300">NIFOG National Channels</h3>
              </div>
              <p className="text-sm text-emerald-200/70">
                These are official federal interoperability channels from CISA SAFECOM. 
                They work nationwide and are 100% reliable for emergency communications.
              </p>
            </div>
            
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-teal-400" />
              </div>
            ) : (
              <div className="grid gap-3">
                {filteredDisplay.map((freq, idx) => (
                  <FrequencyCard 
                    key={`${freq.frequency}-${idx}`} 
                    freq={freq} 
                    onVote={handleVote}
                    isAuthenticated={!!token}
                  />
                ))}
              </div>
            )}
          </TabsContent>
          
          {/* Browse by state tab */}
          <TabsContent value="browse" className="space-y-4">
            {!canAccessAllStates ? (
              <LockedStateFrequencies />
            ) : (
              <>
                <div className="flex gap-4 mb-4">
                  <Select value={selectedState} onValueChange={setSelectedState}>
                    <SelectTrigger className="w-[250px] bg-slate-800 border-slate-700">
                      <SelectValue placeholder="Select a state..." />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {states.map(s => (
                        <SelectItem key={s.code} value={s.code}>
                          {s.name} {s.frequency_count > 0 && `(${s.frequency_count})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {!selectedState ? (
                  <div className="text-center py-12 text-slate-400">
                    <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Select a state to browse frequencies</p>
                  </div>
                ) : loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-teal-400" />
                  </div>
                ) : filteredDisplay.length === 0 ? (
                  <div className="text-center py-12 text-slate-400">
                    <Radio className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No frequencies found for this state</p>
                    <p className="text-sm mt-2">Be the first to submit one!</p>
                  </div>
                ) : (
                  <div className="grid gap-3">
                    {filteredDisplay.map((freq, idx) => (
                      <FrequencyCard 
                        key={`${freq.frequency}-${idx}`} 
                        freq={freq} 
                        onVote={handleVote}
                        isAuthenticated={!!token}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </TabsContent>
          
          {/* Search results tab */}
          <TabsContent value="search" className="space-y-4">
            {searchResults && (
              <>
                <div className="text-sm text-slate-400 mb-4">
                  Found {searchResults.total} results for "{searchResults.query}"
                  {searchResults.filters?.state && ` in ${searchResults.filters.state}`}
                </div>
                
                {filteredDisplay.length === 0 ? (
                  <div className="text-center py-12 text-slate-400">
                    <Search className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No frequencies match your search</p>
                  </div>
                ) : (
                  <div className="grid gap-3">
                    {filteredDisplay.map((freq, idx) => (
                      <FrequencyCard 
                        key={`${freq.frequency}-${idx}`} 
                        freq={freq} 
                        onVote={handleVote}
                        isAuthenticated={!!token}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Submit modal */}
      <SubmitFrequencyModal
        states={states}
        onSubmit={handleSubmit}
        isOpen={submitModalOpen}
        onOpenChange={setSubmitModalOpen}
      />
    </div>
  );
};

export default FrequencyBrowser;
